import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

import MainLayout from "../../../../components/layout/MainLayout";
import Breadcrumbs from "../../../../components/common/Breadcrumbs";
import PsychForm from "./PsychForm";
import { fetchNurses } from "../../../../store/slices/nursingRecordsSlice"; // ✅ Import this
import {
    fetchPsychiatricForm,
    updatePsychiatricForm
} from "../../../../store/slices/psychiatricFormSlice";

const EditPsych = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentPsychiatricForm: singlePsychiatricForm, isLoading } = useSelector(
        (state) => state.psychiatricForm
    );

    const { nurses } = useSelector((state) => state.nursingRecords);

    const [formData, setFormData] = useState({});
    const [rows, setRows] = useState([]);

    // -------------------------------
    // 1️⃣ Fetch form by ID on mount
    // -------------------------------
    useEffect(() => {
        dispatch(fetchPsychiatricForm(id));
        dispatch(fetchNurses()); // ✅ Fetch dynamic nurses list
    }, [dispatch, id]);

    // --------------------------------------------------------
    // 2️⃣ When fetched data arrives → map it into form state
    // --------------------------------------------------------
    useEffect(() => {
        if (!singlePsychiatricForm) return;

        // MAIN table mapping
        setFormData({
            caseId: singlePsychiatricForm.case_id || "",
            patientId: singlePsychiatricForm.patient_id || "",
            name: singlePsychiatricForm.name || "",
            mrNo: singlePsychiatricForm.mr_no || "",
            roomNo: singlePsychiatricForm.room_no || "",
            consultant: singlePsychiatricForm.consultant || "",
            documentName: singlePsychiatricForm.document_name || "",
            effectiveDate: singlePsychiatricForm.effective_date || "",
            approvedBy: singlePsychiatricForm.approved_by || "",
            approvedDate: singlePsychiatricForm.approved_date || "",
            nextRevisionBy: singlePsychiatricForm.next_revision_by || "",
            nextRevisionDate: singlePsychiatricForm.next_revision_date || ""
        });

        // CHILD repeater mapping
        const mappedOrders = singlePsychiatricForm.orders?.map((item) => ({
            id: item.id,
            date_time: item.date_time || "",
            order: item.order || "",
            nurse: item.nurse || "",
            nurse_id: item.nurse_id || null
        })) || [];

        setRows(mappedOrders.length ? mappedOrders : [{ id: Date.now(), date_time: "", order: "", nurse: "", nurse_id: null}]);
    }, [singlePsychiatricForm]);

    // -------------------------------
    // Handle Form Input Change
    // -------------------------------
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // -------------------------------
    // 3️⃣ Submit updated form
    // -------------------------------
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Convert camelCase → snake_case
        const snakeForm = {
            patient_id: formData.patientId,
            case_id: formData.caseId,
            name: formData.name,
            mr_no: formData.mrNo,          // <-- map camelCase to snake_case
            room_no: formData.roomNo,
            consultant: formData.consultant,
            document_name: formData.documentName,
            effective_date: formData.effectiveDate,
            approved_by: formData.approvedBy,
            approved_date: formData.approvedDate,
            next_revision_by: formData.nextRevisionBy,
            next_revision_date: formData.nextRevisionDate,
        };

        // Map child rows
        const ordersPayload = rows.map((r) => ({
            date_time: r.date_time,
            order: r.order,
            nurse: r.nurse,
            nurse_id: r.nurse_id
        }));

        const payload = {
            ...snakeForm,
            orders: ordersPayload
        };

        try {
            const result = await dispatch(
                updatePsychiatricForm({ case_id: caseId, formData: payload })
            ).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Psychiatric form has been updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });

            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });

        } catch (error) {
            Swal.fire("Error", "Something went wrong while updating.", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <PsychForm
                                formData={formData}
                                handleChange={handleChange}
                                rows={rows}
                                setRows={setRows}
                                nurses={nurses}
                            />

                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={isLoading}
                                    >
                                        <i className="ri-edit-2-line me-1"></i>
                                        {isLoading ? "Updating..." : "Update"}
                                    </button>
                                </div>
                            </div>

                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditPsych;
